<template>
  <div class="table">
    <artmisads-table
      :columns="columns"
      row-key="id"
      :scroll="{ x: props.activeTab === '0' ? 1200 : 1000, y: 500 }"
      :data-source="props.dataList"
      :pagination="{
        total: props.tableTotal,
        current: props.params.page,
        pageSize: props.params.size,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100', '200'],
      }"
      :change="onChangePage"
      rowClassName="table-item"
    >
      <template #bodyCell="{ column, text, record }">
        <template v-if="column.name === 'showCurrency'">
          {{ text ? AMAZON_CURRENCY_MAPPER[props.marketplace] : '' }}{{ text }}
        </template>
        <template v-if="column.name === 'reportDateTimestamp'">
          {{ moment(text).format('YYYY-MM-DD') }}
        </template>
        <template v-if="column.name === 'ACOS'">
          {{
            text
              ? (text * 100)
                  .toFixed(2)
                  .replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1') + '%'
              : '0'
          }}
        </template>
        <template v-if="column.name === 'more'">
          <svg
            @click="() => onClickTableChart(record)"
            class="icon chart-icon"
            aria-hidden="true"
          >
            <use xlink:href="#icon-chart"></use>
          </svg>
        </template>
      </template>
      <template #summary>
        <a-table-summary-row>
          <a-table-summary-cell class="fixed-left">{{ st('reports.performanceTable.total') }}</a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '0'">
            <a-typography-text>{{
              props.tagNumParams.totalDetailPageViews
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '0'">
            <a-typography-text>{{
              props.tagNumParams.totalAddToCart
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '0'">
            <a-typography-text>{{
              props.tagNumParams.totalConversions
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '1'">
            <a-typography-text>{{
              props.tagNumParams.totalQuantity
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell>
            <a-typography-text
              >{{
                props.tagNumParams.totalSalesAmount
                  ? AMAZON_CURRENCY_MAPPER[props.marketplace]
                  : ''
              }}{{ props.tagNumParams.totalSalesAmount }}</a-typography-text
            >
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '0'">
            <a-typography-text>{{
              props.tagNumParams.totalAcos
                ? (props.tagNumParams.totalAcos * 100)
                    .toFixed(2)
                    .replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1') + '%'
                : 0
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '0'">
            <a-typography-text
              >{{
                props.tagNumParams.totalDiscountedSalesAmount
                  ? AMAZON_CURRENCY_MAPPER[props.marketplace]
                  : ''
              }}{{
                props.tagNumParams.totalDiscountedSalesAmount
              }}</a-typography-text
            >
          </a-table-summary-cell>
          <a-table-summary-cell>
            <a-typography-text
              >{{
                props.tagNumParams.totalCommissionAndFeeAmount
                  ? AMAZON_CURRENCY_MAPPER[props.marketplace]
                  : ''
              }}{{
                props.tagNumParams.totalCommissionAndFeeAmount
              }}</a-typography-text
            >
          </a-table-summary-cell>
          <a-table-summary-cell v-if="props.activeTab === '1'">
            <a-typography-text>{{
                props.tagNumParams.totalFinalCommissionAndFeeAmount
                  ? AMAZON_CURRENCY_MAPPER[props.marketplace]
                  : ''
              }}{{
              props.tagNumParams.totalFinalCommissionAndFeeAmount
            }}</a-typography-text>
          </a-table-summary-cell>
          <a-table-summary-cell>
            <a-typography-text></a-typography-text>
          </a-table-summary-cell>
        </a-table-summary-row>
      </template>
    </artmisads-table>
  </div>
</template>
<script setup lang="jsx">
import moment from 'moment'
import { computed } from 'vue'

import { AMAZON_CURRENCY_MAPPER } from '~/contants/common'
import { useSellerI18n } from '@/composables/useSellerI18n'

const { st } = useSellerI18n()

const props = defineProps([
  'dataList',
  'activeTab',
  'marketplace',
  'tableTotal',
  'params',
  'setParams',
  'onClickTableChart',
  'tagNumParams',
])

const onChangePage = (data, filters, sorter) => {
  const { current, pageSize } = data
  let newParams = { ...props.params }
  newParams.page = current
  newParams.size = pageSize
  if (sorter) {
    const { field, order } = sorter
    newParams.sortField = field
    newParams.sortDirection = order === 'ascend' ? 'asc' : 'desc'
  } else {
    newParams.sortField = ''
    newParams.sortDirection = ''
  }
  props.setParams(newParams)
}

const productColumnRender = ({ text, record }) => (
  <a
    target="_blank"
    href={`/seller/product/${record.productId}`}
    class="product-item">
    <img src={record.productImageUrl} />
    <div class="text">
      <p class="title">{text}</p>
      <span class="asin">{record.productAsin}</span>
    </div>
  </a>
)

const columns = computed(() => {
  if (props.activeTab === '0') {
    return [
      {
        title: st('reports.filterBar.product'),
        fixed: 'left',
        dataIndex: 'productTitle',
        width: 320,
        customRender: productColumnRender,
      },
      {
        title: st('reports.performanceTable.dpvs'),
        dataIndex: 'detailPageViews',
        width: 80,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.addToCart'),
        dataIndex: 'addToCart',
        width: 130,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.conversions'),
        dataIndex: 'conversions',
        width: 120,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.sales'),
        dataIndex: 'salesAmount',
        name: 'showCurrency',
        width: 110,
        sorter: true,
      },
      {
        name: 'ACOS',
        title: st('reports.performanceTable.acos'),
        dataIndex: 'acos',
        width: 100,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.salesForCommission'),
        dataIndex: 'discountedSalesAmount',
        name: 'showCurrency',
        width: 202,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.commission'),
        dataIndex: 'commissionAndFeeAmount',
        name: 'showCurrency',
        width: 118,
        sorter: true,
      },
      {
        title: st('reports.performanceTable.more'),
        name: 'more',
        dataIndex: 'commissionAndFeeAmount',
        width: 70,
        fixed: 'right',
      },
    ]
  }
  return [
    {
      title: st('reports.filterBar.product'),
      dataIndex: 'productTitle',
      customRender: productColumnRender,
    },
    {
      title: st('reports.performanceTable.quantity'),
      dataIndex: 'quantity',
      sorter: true,
    },
    {
      title: st('reports.performanceTable.sales'),
      dataIndex: 'salesAmount',
      name: 'showCurrency',
      sorter: true,
    },
    {
      title: st('reports.performanceTable.commission'),
      dataIndex: 'commissionAndFeeAmount',
      name: 'showCurrency',
      sorter: true,
    },
    {
      title: st('reports.performanceTable.adjustedCommissionByAcc'),
      dataIndex: 'finalCommissionAndFeeAmount',
      name: 'showCurrency',
      sorter: true,
    },
    {
      title: st('reports.performanceTable.more'),
      name: 'more',
      dataIndex: 'commissionAndFeeAmount',
      width: 70,
      fixed: 'right',
    },
  ]
})
</script>
<style lang="less" scoped>
:deep(.ant-table) {
  .chart-icon {
    cursor: pointer;
    width: 20px;
    height: 20px;
  }

  table {
    table-layout: fixed !important;
  }

  .inactive-commission {
    color: var(--dark-4);
  }

  .status-text {
    text-transform: capitalize;
  }

  .product-item {
    display: flex;

    img {
      width: 56px;
      height: 56px;
      margin-right: 12px;
      border-radius: 8px;
      object-fit: contain;
    }

    .text {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      justify-content: center;

      .title {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--dark-1);
        font-weight: 500;
        font-size: var(--font-size-middle);
        text-decoration: underline;
      }

      .asin {
        color: var(--dark-3);
        font-weight: 500;
        font-size: var(--font-size-min);
        letter-spacing: 0.3px;
        text-align: right;
        line-height: 16px;
        text-align: left;
        margin-top: 2px;
      }
    }
  }
}

.fixed-left {
  left: 0;
  z-index: 1;
  position: sticky;
  padding-left: 86px;
}
</style>
